import { Component, OnInit, OnDestroy } from '@angular/core';
import { AnalyticsService } from '../core/services/analytics.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { NavigationEnd, Router } from '@angular/router';
import { DataService } from '../core/services/data.service';
import { AuthService } from '../core/services/auth.service';
import { EncryptionService } from '../core/services/encryption.service';
import { Platform } from '@angular/cdk/platform';
import { LogToS3Service } from '../core/services/log.service';
import { VersionService } from '../core/services/version.service';
import { Device } from '@capacitor/device';
import { EventService } from '../core/services/event.service';
import { SessionService } from '../core/services/session.service';
import { MatDialog } from '@angular/material/dialog';
import { NotificationService, AppNotification } from '../core/services/notification.service';
import { ThemeService } from '../core/services/theme.service';
import { FEATURE_USER_BRANDING, isFeatureEnabled } from '../utils/feature.utils';
import { goToSessionlessLanding } from '../core/utils/auth-landing';
import { environment } from '../../environments/environment';
import { reloadApp } from '../core/utils/reload-app';

@Component({
  selector: 'app-top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.css']
})
export class TopBarComponent implements OnInit, OnDestroy {
  private authStateSub: Subscription | null = null;
  private routerEventsSub: Subscription | null = null;
  private readonly topNavMenuPaths = ['/home', '/invitation', '/autopilot', '/how-to-videos', '/reels'];
  showTopNavMenu: boolean = false;
  // Hidden in production builds — see environment.features. ThemeService pins the
  // app to light whenever this is off, so hiding it can't strand anyone in dark.
  readonly showThemeSwitch: boolean = environment.features.themeSwitcher;
  version: string;
  user: any;
  displayName: string | null | undefined;
  email: string | null | undefined;
  user_image: any;
  currentUrl: string = '';
  hostImageError: boolean = false;
  photoWallAccess: any;
  brandingAccess: boolean = false;
  publicAccess: any;
  isGuestUser: boolean = false;
  showLoginPopup: boolean = false;
  showNotifications: boolean = false;
  notifications: AppNotification[] = [];
  unreadCount: number = 0;
  showReviewModal: boolean = false;
  selectedNotification: AppNotification | null = null;
  isReviewLoading: boolean = false;
  isStorageFull: boolean = false;
  storageAvailable: boolean = true;
  _eventSizeBytes: number | null = null;
  _availableBytes: number | null = null;

  constructor(public router: Router,
    private afAuth: AngularFireAuth,
    public dataService: DataService,
    private authService: AuthService,
    private platform: Platform,
    private eventService: EventService,
    private loggingService: LogToS3Service,
    private versionService: VersionService,
    private sessionService: SessionService,
    public dialog: MatDialog,
    public notificationService: NotificationService,
    public themeService: ThemeService,
    private analytics: AnalyticsService,
  ) {

    this.version = this.versionService.version;
    this.showTopNavMenu = this.topNavMenuPaths.includes(this.router.url.split('?')[0].split('#')[0]);
    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.showTopNavMenu = this.topNavMenuPaths.includes(event.urlAfterRedirects.split('?')[0].split('#')[0]);
      });
    this.currentUrl = new URL(window.location.href).toString();
    // console.log('Current URL:', this.currentUrl); //__CONSOLE_LOG__ //__CONSOLE_LOG__ //__CONSOLE_LOG__ //__CONSOLE_LOG__

    // The public shared-flipbook page. Handled before the branch below rather
    // than inside its route list, because that branch also writes
    // dataService.showTopBar = false — and that setter persists to
    // localStorage, so it would keep the top bar hidden on every route
    // afterwards, including the /home this page's CTA sends people to.
    //
    // Without this early return the authState subscription below sees a null
    // user (which is the normal, permanent state for someone opening a shared
    // link) and redirects them to /auth/login.
    if (this.currentUrl.includes('/flipbook/')) {
      this.user = null;
      return;
    }

    if (this.currentUrl.includes('open') ||
    this.currentUrl.includes('live') || 
    this.currentUrl.includes('token') || 
    this.currentUrl.includes('guest/') || 
    this.currentUrl.includes('demo-event') || 
    this.currentUrl.includes('demo-landing') || 
    this.currentUrl.includes('demo-direct-gallery')) {
      this.user = null;
      // console.log('Open/guest event detected, hiding top bar'); //__CONSOLE_LOG__ //__CONSOLE_LOG__
      this.dataService.showTopBar = false;
      // Don't check auth state or redirect for guest/open routes
    }
    else {
      this.dataService.showTopBar = true;
      this.authStateSub = this.afAuth.authState.subscribe((user) => {
        //  console.log(localStorage.getItem('user'));
        this.user = user;
        this.displayName = user?.displayName;
        this.email = user?.email;
        const onDemoPage = (this.router.url || '').includes('demo-event') || (this.router.url || '').includes('demo-landing') || (this.router.url || '').includes('demo-direct-gallery');
        if (onDemoPage) return;
        const linkInProgress = localStorage.getItem('linkGuestInProgress') === 'true';
        if (linkInProgress) return;
        if (user) {
          if (user.isAnonymous) {
            this.afAuth.signOut();
            this.router.navigateByUrl("/auth/login", { replaceUrl: true });
          } else {
            // Load account details when user logs in
            this.loadAccountDetails();
          }
        } else {
          // Never redirect demo or guest users to login — they use custom-token auth
          const isDemoUser = localStorage.getItem('isDemoUser') === 'true';
          const isGuestUser = localStorage.getItem('isGuestUser') === 'true';
          if (isDemoUser || isGuestUser) return;
          localStorage.setItem("user", "null");
          JSON.parse(localStorage.getItem("user")!);
          // Not a literal '/auth/login': with deferred login on, the front door
          // for a sessionless visitor is /auth/start. The helper also refuses to
          // navigate to the route we are already on, which matters here — this
          // fires on every cold load and the router reloads same-URL navigations.
          goToSessionlessLanding(this.router);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.authStateSub?.unsubscribe();
    this.routerEventsSub?.unsubscribe();
  }

  /**
   * Reels is a marketing page, so the click is the only signal we get — there
   * is no downstream conversion event to infer interest from. Captured on the
   * anchor rather than on navigation so a click still registers when the user
   * is already on /reels and the router does nothing.
   */
  onReelsNavClick(): void {
    this.analytics.capture('reels_tab_clicked', {
      source: 'top_nav',
      from_url: this.router.url,
    });
  }

  onHostImageError() {
    this.hostImageError = true;
  }

  refreshPage() {
    reloadApp('manual_refresh', { force: true });
  }


  async ngOnInit() {
    this.currentUrl = new URL(window.location.href).toString();
    // console.log('Current URL:', this.currentUrl); //__CONSOLE_LOG__ //__CONSOLE_LOG__
    if (this.currentUrl.includes('open') || this.currentUrl.includes('live') || this.currentUrl.includes('splash') || this.currentUrl.includes('guest/') || this.currentUrl.includes('demo-event') || this.currentUrl.includes('demo-landing') || this.currentUrl.includes('demo-direct-gallery')) {
      this.dataService.showTopBar = false;
      // this.router.navigateByUrl('/open-event-invite/' + this.currentUrl.split('open-event-invite/')[1]);
    } else {
      this.dataService.showTopBar = true;
      this.sessionService.startSession();
      const isGuest = localStorage.getItem('isGuestUser') === 'true' || localStorage.getItem('isDemoUser') === 'true';
      if (isGuest) {
        this.isGuestUser = true;
      }
      else {
        this.isGuestUser = false;
        await this.loadAccountDetails();
      }
      this.notificationService.notifications$.subscribe(n => {
        this.notifications = n;
        this.unreadCount = this.notificationService.unreadCount;
      });
      this.loadNotifications();
    }
  }

  async loadNotifications() {
    try {
      const obs = await this.eventService.getNotifications();
      obs.subscribe({
        next: (res: any) => {
          // console.log('[Notifications] Raw API response:', JSON.stringify(res) //__CONSOLE_LOG__);
          this.storageAvailable = res?.eventStorageSize?.available ?? true;
          this._eventSizeBytes = res?.eventStorageSize?.event_size_bytes ?? null;
          this._availableBytes = res?.eventStorageSize?.available_bytes ?? null;
          const asRequestedHost: any[] =
            res?.host_transitions?.as_requested_host ||
            res?.notifications?.host_transitions?.as_requested_host || [];
          // console.log('[Notifications] as_requested_host:', asRequestedHost); //__CONSOLE_LOG__
          if (asRequestedHost.length) {
            const mapped: AppNotification[] = asRequestedHost.map((n: any) => ({
              id: n.id?.toString() || Math.random().toString(36).slice(2),
              type: 'host_transfer_request',
              title: 'Host Transfer Request',
              message: `${n.host_name || 'Someone'} wants you to become host of "${n.event_name || 'an event'}".`,
              event_id: n.event_id?.toString(),
              event_name: n.event_name,
              from_user_id: n.requested_host_id?.toString(),
              from_user_name: n.host_name,
              host_id: n.host_id?.toString(),
              transition_request_id: n.id?.toString(),
              user_name: n.requested_host_name,
              created_at: n.created_date,
              read: false,
              eventStorageSize: n.eventStorageSize ?? null,
            }));
            this.notificationService.setNotifications(mapped);
          } else {
            // console.log('[Notifications] No pending host transfer requests found.'); //__CONSOLE_LOG__
          }
        },
        error: (err: any) => console.error('[Notifications] Failed to load notifications', err),
      });
    } catch (err) {
      // console.error('[Notifications] Error:', err); //__CONSOLE_LOG__
    }
  }

  toggleNotifications() {
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      this.notificationService.markAllRead();
    }
  }

  get hostTransferNotifications(): AppNotification[] {
    return this.notifications.filter(n => n.type === 'host_transfer_request');
  }

  get otherNotifications(): AppNotification[] {
    return this.notifications.filter(n => n.type !== 'host_transfer_request');
  }

  formatNotifTime(ts: any): string {
    if (!ts) return '';
    const date = new Date(+ts * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    const day = date.getDate();
    const mon = date.toLocaleString('en-GB', { month: 'short' });
    const yr = date.getFullYear();
    const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(' ', '');
    return `${day} ${mon} ${yr}  ${time}`;
  }

  openReview(n: AppNotification) {
    this.showNotifications = false;
    const storage = n.eventStorageSize;
    const storageAvailable = storage?.available ?? true;
    const eventSizeBytes = storage?.event_size_bytes ?? null;
    const availableBytes = storage?.available_bytes ?? null;
    this.router.navigate(['/host-transfer'], {
      state: { notification: n, storageAvailable, eventSizeBytes, availableBytes },
      queryParams: { _t: Date.now() }  // Force re-navigation when already on /host-transfer
    });
  }

  closeReviewModal() {
    this.showReviewModal = false;
    this.selectedNotification = null;
    this.isStorageFull = false;
  }

  async acceptHostTransfer() {
    if (!this.selectedNotification) return;
    this.isReviewLoading = true;
    try {
      const obs = await this.eventService.respondToHostTransition(
        this.selectedNotification.event_id!,
        this.selectedNotification.from_user_id!,
        this.selectedNotification.host_id!,
        this.selectedNotification.event_name!,
        this.selectedNotification.user_name!,
        this.selectedNotification.transition_request_id!,
        'accepted'
      );
      obs.subscribe({
        next: () => {
          this.notificationService.remove(this.selectedNotification!.id);
          this.closeReviewModal();
        },
        error: (err: any) => console.error('Accept failed', err),
        complete: () => { this.isReviewLoading = false; }
      });
    } catch (e) {
      // console.error(e); //__CONSOLE_LOG__
      this.isReviewLoading = false;
    }
  }

  async rejectHostTransfer() {
    if (!this.selectedNotification) return;
    this.isReviewLoading = true;
    try {
      const obs = await this.eventService.respondToHostTransition(
        this.selectedNotification.event_id!,
        this.selectedNotification.from_user_id!,
        this.selectedNotification.host_id!,
        this.selectedNotification.event_name!,
        this.selectedNotification.user_name!,
        this.selectedNotification.transition_request_id!,
        'declined'
      );
      obs.subscribe({
        next: () => {
          this.notificationService.remove(this.selectedNotification!.id);
          this.closeReviewModal();
        },
        error: (err: any) => console.error('Reject failed', err),
        complete: () => { this.isReviewLoading = false; }
      });
    } catch (e) {
      // console.error(e); //__CONSOLE_LOG__
      this.isReviewLoading = false;
    }
  }

  /**
   * Guest/demo state read straight from storage rather than from `isGuestUser`.
   * The field is only set in ngOnInit, but the constructor's authState
   * subscription can call loadAccountDetails() before that runs — so a demo
   * guest on /event-detail/true would otherwise slip through and hit the host
   * account endpoint.
   */
  private get isGuestOrDemoSession(): boolean {
    return localStorage.getItem('isGuestUser') === 'true'
      || localStorage.getItem('isDemoUser') === 'true'
      || sessionStorage.getItem('isGuestUser') === 'true';
  }

  async loadAccountDetails() {
    if ((this.router.url || '').includes('demo-event') || this.isGuestUser || this.isGuestOrDemoSession) return;
    try {
      (await (this.eventService.getMyAccountDetails())).subscribe({
        next: (resp: any) => {
          // console.log({ resp }); //__CONSOLE_LOG__ //__CONSOLE_LOG__

          if (resp) {
            this.displayName = resp.account.name;
            this.email = resp.account.email;
            this.photoWallAccess = resp.account.photowall;
            // Branding link is gated by the `user_branding` feature flag, with the
            // old account column as the fallback until the backend ships the key.
            this.brandingAccess = isFeatureEnabled(
              resp.features,
              FEATURE_USER_BRANDING,
              () => resp.account.branding === 1
            );
            this.publicAccess = resp.account.public_access;
            this.user_image = resp.account.image;
            if (resp.account.image === 'File does not exist') {
              // ngOnInit can call this before authState has emitted, so `user` may
              // still be unset — fall back to the auth photo once it lands rather
              // than leaving the avatar blank.
              this.user_image = this.user?.photoURL ?? this.user?.photoUrl;
              if (!this.user_image) {
                this.afAuth.currentUser
                  .then((u) => { this.user_image = u?.photoURL ?? null; })
                  .catch(() => { this.user_image = null; });
              }
            }

            // Re-identify PostHog with analytics_id to match iOS/Android.
            // No reset() — identify() alone switches/merges into the analytics_id person;
            // reset() would spawn a fresh anonymous id and split the session.
            const analyticsId = resp.account.analytics_id;
            if (analyticsId && localStorage.getItem('analytics_id') !== analyticsId) {
              localStorage.setItem('analytics_id', analyticsId);
              this.analytics.identify(analyticsId);
            }

          }
        },
        error: (err) => {
          // console.error(err); //__CONSOLE_LOG__ //__CONSOLE_LOG__
        },
      });

    } catch (err: any) {
      // console.error(err);
    }
  }

  async login() {
    const currentUser = await this.afAuth.currentUser;
    if (!currentUser) {
      throw new Error("No authenticated user found.");
    }
    // console.log(currentUser.uid); //__CONSOLE_LOG__
    sessionStorage.setItem('isGuestUser', 'true');
    sessionStorage.setItem('event_id', localStorage.getItem('e')!);
    sessionStorage.setItem('uid', currentUser.uid.toString());
    // Open the login popup for guest users
    this.authService
      .signOut()
      .then((res) => {
        this.router.navigateByUrl(
          "/auth/login"
        );
      })
      .catch((err) => {
        // console.error(err); //__CONSOLE_LOG__
      });
    //  this.showLoginPopup = true;
  }

  closeLoginPopup() {
    this.showLoginPopup = false;
  }
  goHome() {

    if (localStorage.getItem('eventId')) {
      localStorage.removeItem('eventId');
    }
    if (localStorage.getItem('showTimerCard')) {
      localStorage.removeItem('showTimerCard');
    }
    if (localStorage.getItem('showInviteCard')) {
      localStorage.removeItem('showInviteCard');
    }
    if (localStorage.getItem('access')) {
      localStorage.removeItem('access');
    }
    if (localStorage.getItem('isEventId')) {
      localStorage.removeItem('isEventId');
    }
    if (localStorage.getItem('event_hash')) {
      localStorage.removeItem('event_hash');
    }

    if (localStorage.getItem('event_name')) {
      localStorage.removeItem('event_name');
    }

    if (localStorage.getItem('e')) {
      localStorage.removeItem('e');
    }

    if (localStorage.getItem('isHost')) {
      localStorage.removeItem('isHost');
    }

    if (localStorage.getItem('inviteData')) {
      localStorage.removeItem('inviteData');
    }

    if (localStorage.getItem('eventData')) {
      localStorage.removeItem('eventData');
    }

    if (localStorage.getItem('isScheduled')) {
      localStorage.removeItem('isScheduled');
    }

    if (localStorage.getItem('qrcode')) {
      localStorage.removeItem('qrcode');
    }
    if (localStorage.getItem('savedHashtags')) {
      localStorage.removeItem('savedHashtags');
    }
    if (localStorage.getItem('name')) {
      localStorage.removeItem('name');
    }
    if (localStorage.getItem('invite_resp')) {
      localStorage.removeItem('invite_resp');
    }
    if (localStorage.getItem('event_subfolders')) {
      localStorage.removeItem('event_subfolders');
    }
    if (localStorage.getItem('web_qrImage')) {
      localStorage.removeItem('web_qrImage');
    }
    if (localStorage.getItem('web_viewer_qrImage')) {
      localStorage.removeItem('web_viewer_qrImage');
    }
    if (localStorage.getItem('coverImage')) {
      localStorage.removeItem('coverImage');
    }

    if (localStorage.getItem('openPopup')) {
      localStorage.removeItem('openPopup');
    }
    if (localStorage.getItem('formData')) {
      localStorage.removeItem('formData');
    }
    if (localStorage.getItem('isFutureEvent')) {
      localStorage.removeItem('isFutureEvent');
    }
    if (localStorage.getItem('showGuestInvite')) {
      localStorage.removeItem('showGuestInvite');
    }
    if (localStorage.getItem('redirectUrl')) {
      localStorage.removeItem('redirectUrl');
    }

    this.router.navigate(['/home']);
  }

  async downloadApp() {
    const info = await Device.getInfo();

    if (this.platform.IOS && info.model === 'iPhone' && info.platform === 'web') {
      window.location.href = 'https://apps.apple.com/in/app/picaggo/id1527686260';
    }

    if (this.platform.ANDROID && info.platform === 'web') {
      window.location.href = 'https://play.google.com/store/apps/details?id=application.share.photo.picaggo';
    }

    if (!this.platform.ANDROID && !this.platform.IOS && info.platform === 'web') {
      window.location.href = 'https://picaggo.com';
    }
  }

  logout() {
    this.loggingService.log("log-out called");
    this.authService
      .signOut()
      .then((res) => {
        this.router.navigateByUrl(
          "/auth/login"
        );
      })
      .catch((err) => {
        // console.error(err); //__CONSOLE_LOG__
      });
  }

}
